import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Trophy, Zap, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PipelineStepper, { STEP_META } from "@/components/simulator/PipelineStepper";
import StepFeedback from "@/components/simulator/StepFeedback";
import ConnectStep from "@/components/simulator/ConnectStep";
import StreamsStep from "@/components/simulator/StreamsStep";
import MappingStep from "@/components/simulator/MappingStep";
import IdentityStep from "@/components/simulator/IdentityStep";
import UnifiedProfileStep from "@/components/simulator/UnifiedProfileStep";
import SegmentInsightStep from "@/components/simulator/SegmentInsightStep";
import ActivateStep from "@/components/simulator/ActivateStep";

const STEPS = ["connect", "streams", "mapping", "identity", "unified", "segment_insight", "activate"];

const emptyAnswers = () => ({
  connect: [], streams: {}, mapping: {}, identity: [],
  segment_insight: { segment: {}, insight_id: null }, activate: { channel_id: null },
});

export default function SimulatorScenarioPage({ apiPrefix = "/simulator", backPath = "/simulator", terms = {}, stepMeta = STEP_META }) {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(emptyAnswers());
  const [results, setResults] = useState({});
  const [checking, setChecking] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completion, setCompletion] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    setStepIndex(0);
    setAnswers(emptyAnswers());
    setResults({});
    setCompletion(null);
    api.get(`${apiPrefix}/scenarios/${scenarioId}`).then(({ data }) => {
      setScenario(data);
      const done = data.steps_completed || {};
      const nextAnswers = emptyAnswers();
      const nextResults = {};
      for (const step of STEPS) {
        if (!done[step]) continue;
        const ans = done[step].answer || {};
        if (step === "connect") nextAnswers.connect = ans.selected_sources || [];
        else if (step === "streams") nextAnswers.streams = ans.stream_choices || {};
        else if (step === "mapping") nextAnswers.mapping = ans.mapping || {};
        else if (step === "identity") nextAnswers.identity = ans.selected_rules || [];
        else if (step === "segment_insight") nextAnswers.segment_insight = { segment: ans.segment || {}, insight_id: ans.insight_id || null };
        else if (step === "activate") nextAnswers.activate = { channel_id: ans.channel_id || null };
        nextResults[step] = { score: done[step].score, ai_feedback: done[step].ai_feedback, is_correct: done[step].score >= 80 };
      }
      setAnswers(nextAnswers);
      setResults(nextResults);
    }).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load this simulation"))
      .finally(() => setLoading(false));
  }, [scenarioId, apiPrefix]);

  const currentStep = STEPS[stepIndex];

  const buildPayload = () => {
    if (currentStep === "connect") return { selected_sources: answers.connect };
    if (currentStep === "streams") return { stream_choices: answers.streams };
    if (currentStep === "mapping") return { mapping: answers.mapping };
    if (currentStep === "identity") return { selected_rules: answers.identity };
    if (currentStep === "unified") return {};
    if (currentStep === "segment_insight") return { segment: answers.segment_insight.segment, insight_id: answers.segment_insight.insight_id };
    if (currentStep === "activate") return { channel_id: answers.activate.channel_id };
    return {};
  };

  const checkStep = async () => {
    setChecking(true);
    try {
      const { data } = await api.post(`${apiPrefix}/scenarios/${scenarioId}/steps/${currentStep}`, buildPayload());
      setResults((prev) => ({ ...prev, [currentStep]: data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to check this step");
    } finally {
      setChecking(false);
    }
  };

  const completeSimulation = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`${apiPrefix}/scenarios/${scenarioId}/complete`);
      setCompletion(data);
      refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't complete the simulation — please try again.");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="simulator-scenario-loading"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="simulator-scenario-error">
        <p className="text-slate-300">{error}</p>
        <Link to={backPath} className="text-indigo-400 hover:text-indigo-300 text-sm">Back to Simulator</Link>
      </div>
    );
  }

  if (completion) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="simulator-completion-screen">
        <Trophy className="w-16 h-16 text-[#FBBF24] mx-auto mb-4" />
        <h1 className="font-heading text-3xl font-semibold text-white">Simulation Complete!</h1>
        <p className="text-slate-400 mt-2">{scenario.title}</p>
        <div className="flex items-center justify-center gap-8 mt-8">
          <div className="text-center"><p className="font-heading text-3xl font-bold text-indigo-400" data-testid="completion-total-score">{completion.total_score}%</p><p className="text-xs text-slate-400 mt-1">Total Score</p></div>
          <div className="text-center"><p className="font-heading text-3xl font-bold text-[#FBBF24] flex items-center gap-1 justify-center"><Zap className="w-6 h-6" />{completion.xp_awarded}</p><p className="text-xs text-slate-400 mt-1">XP Earned</p></div>
        </div>
        {completion.newly_earned_achievements?.length > 0 && (
          <p className="text-sm text-[#FBBF24] mt-6" data-testid="completion-badge-earned">🏆 New badge unlocked: {completion.newly_earned_achievements[0].title}</p>
        )}
        <div className="flex items-center justify-center gap-3 mt-8">
          <button onClick={() => navigate(backPath)} data-testid="completion-back-to-list-btn" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
            Back to Simulator
          </button>
          {completion.next_scenario_id && (
            <button onClick={() => navigate(`${backPath}/${completion.next_scenario_id}`)} data-testid="completion-next-scenario-btn" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white transition-colors">
              Next Scenario →
            </button>
          )}
        </div>
      </div>
    );
  }

  const connectedNames = answers.connect.map((id) => scenario.sources.find((s) => s.id === id)?.name).filter(Boolean);
  const identityLabels = answers.identity.map((id) => scenario.identity_rule_options.find((o) => o.id === id)?.label).filter(Boolean);
  const result = results[currentStep];
  const isLast = stepIndex === STEPS.length - 1;
  const allStepsChecked = STEPS.every((s) => !!results[s]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="simulator-scenario-page">
      <button onClick={() => navigate(backPath)} data-testid="back-to-simulator-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Simulator
      </button>

      <div className="glass-panel rounded-2xl p-6 mb-6">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">{scenario.industry}</span>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white mt-2" data-testid="simulator-scenario-title">{scenario.title}</h1>
        <p className="text-sm text-slate-400 mt-2">{scenario.description}</p>
      </div>

      <PipelineStepper steps={STEPS} currentStep={currentStep} results={results} onJump={setStepIndex} stepMeta={stepMeta} />

      <div className="glass-panel rounded-2xl p-6">
        {currentStep === "connect" && (
          <ConnectStep sources={scenario.sources} selected={answers.connect} onChange={(v) => setAnswers((p) => ({ ...p, connect: v }))} />
        )}
        {currentStep === "streams" && (
          <StreamsStep sources={scenario.sources} connectedIds={answers.connect} streamOptions={scenario.stream_options} choices={answers.streams} onChange={(v) => setAnswers((p) => ({ ...p, streams: v }))} title={terms.streamsTitle} description={terms.streamsDescription} />
        )}
        {currentStep === "mapping" && (
          <MappingStep sources={scenario.sources} connectedIds={answers.connect} mappableFields={scenario.mappable_fields} targetObjects={scenario.target_objects} mapping={answers.mapping} onChange={(v) => setAnswers((p) => ({ ...p, mapping: v }))} title={terms.mappingTitle} description={terms.mappingDescription} targetSectionLabel={terms.mappingTargetSectionLabel} />
        )}
        {currentStep === "identity" && (
          <IdentityStep options={scenario.identity_rule_options} selected={answers.identity} onChange={(v) => setAnswers((p) => ({ ...p, identity: v }))} title={terms.identityTitle} description={terms.identityDescription} />
        )}
        {currentStep === "unified" && (
          <UnifiedProfileStep connectedNames={connectedNames} mappedCount={Object.keys(answers.mapping).length} identityLabels={identityLabels} title={terms.unifiedTitle} description={terms.unifiedDescription} entityLabel={terms.unifiedEntityLabel} fieldUnitLabel={terms.unifiedFieldUnitLabel} />
        )}
        {currentStep === "segment_insight" && (
          <SegmentInsightStep
            segmentDef={scenario.segment} insightDef={scenario.insight}
            segment={answers.segment_insight.segment} onSegmentChange={(v) => setAnswers((p) => ({ ...p, segment_insight: { ...p.segment_insight, segment: v } }))}
            insightId={answers.segment_insight.insight_id} onInsightChange={(v) => setAnswers((p) => ({ ...p, segment_insight: { ...p.segment_insight, insight_id: v } }))}
            segmentStepTitle={terms.segmentStepTitle} insightStepTitle={terms.insightStepTitle}
          />
        )}
        {currentStep === "activate" && (
          <ActivateStep
            channels={scenario.activation.channels} channelId={answers.activate.channel_id} onChange={(v) => setAnswers((p) => ({ ...p, activate: { channel_id: v } }))}
            summary={{ connectedCount: answers.connect.length, streamsCount: Object.keys(answers.streams).length, mappedCount: Object.keys(answers.mapping).length, identityCount: answers.identity.length }}
            title={terms.activateTitle} description={terms.activateDescription}
          />
        )}

        <StepFeedback result={result} />

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
          <button
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={stepIndex === 0} data-testid="sim-prev-step-btn"
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <button
            onClick={checkStep} disabled={checking} data-testid="sim-check-step-btn"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {result ? "Re-check with AI" : "Check My Work"}
          </button>
          {isLast ? (
            <button
              onClick={completeSimulation} disabled={completing || !allStepsChecked} data-testid="sim-complete-btn"
              className="flex items-center gap-1.5 text-sm font-semibold bg-primary hover:bg-primary/90 disabled:opacity-40 text-white px-5 py-2.5 rounded-lg transition-colors"
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Complete Simulation
            </button>
          ) : (
            <button
              onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))} data-testid="sim-next-step-btn"
              className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
